/**
 * "تصميم الروشتة", reachable from the clinics page.
 *
 * The legacy build put a الروشتة button at the top of the clinic screen: the
 * letterhead is part of how a clinic presents itself, so it belonged next to the
 * clinics rather than buried in account settings. The rebuild had moved it into
 * the settings page, where a doctor setting up a clinic would not think to look.
 *
 * This is a thin wrapper. `PrescriptionDesigner` owns the template and colour
 * choices and its own live preview; all this adds is loading the current values,
 * saving them, and putting the two side by side so a change is visible without
 * asking for it.
 */

import { useEffect, useState } from 'react';
import { Button, Icon, Modal, Spinner } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { doctorApi } from '../../api/endpoints';
import { normalizeDoctor } from '../../domain/catalog';
import { compressImage } from '../../domain/files';
import { PrescriptionDesigner } from '../settings/PrescriptionDesigner';

export function PrescriptionDesignerModal({ open, onClose }) {
  const { doctorProfile, setDoctorProfile } = useAuth();
  const { success, error: toastError } = useNotifications();

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reload from the profile each time it opens, so a cancelled edit does not
  // linger as the starting point of the next one.
  useEffect(() => {
    if (!open || !doctorProfile) return;
    setForm({
      logo: doctorProfile.logo || '',
      prescriptionStyle: doctorProfile.prescriptionStyle || 'wave-top',
      printPrimaryColor: doctorProfile.printColors?.primary || '#06b6d4',
      printSecondaryColor: doctorProfile.printColors?.secondary || '#a855f7',
    });
  }, [open, doctorProfile]);

  if (!open) return null;

  const handleLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUri = await compressImage(file, { maxWidth: 500, maxHeight: 500, quality: 0.85 });
      setForm((current) => ({ ...current, logo: dataUri }));
    } catch (err) {
      toastError(`تعذر قراءة الشعار: ${err.message}`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only the letterhead fields belong to this screen, but the profile route
      // is a whole-object update — so everything else is re-sent unchanged.
      // Omitting them would silently reset a doctor's prices and discounts.
      await doctorApi.updateProfile({
        title: doctorProfile.title || '',
        categoryId: doctorProfile.category || '',
        priceExam: Number(doctorProfile.price_exam ?? 500),
        priceFollowup: Number(doctorProfile.price_followup ?? 200),
        hasNightDiscount: doctorProfile.hasNightDiscount,
        nightDiscountPercent: doctorProfile.nightDiscountPercent,
        hasFirstVisitDiscount: doctorProfile.hasFirstVisitDiscount,
        firstVisitDiscountPercent: doctorProfile.firstVisitDiscountPercent,
        ...form,
      });

      const fresh = await doctorApi.profile();
      setDoctorProfile(normalizeDoctor(fresh));
      success('تم حفظ تصميم الروشتة');
      onClose();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ التصميم');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="تصميم الروشتة"
      icon="file-text"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" loading={saving} disabled={!form} onClick={save}>
            حفظ التصميم
          </Button>
        </div>
      }
    >
      {!form ? (
        <Spinner variant="inline" label="جاري تحميل بيانات الطبيب..." />
      ) : (
        <div className="space-y-6">
          <p className="text-sm leading-relaxed text-slate-400">
            اختر الشكل والألوان — المعاينة تتحدث فوراً مع كل تغيير.
          </p>

          <section className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-4">
            <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-cyan-300">
              <Icon name="image" size={15} />
              شعار العيادة
            </h4>

            <div className="flex flex-wrap items-center gap-4">
              {form.logo && (
                <img
                  src={form.logo}
                  alt="شعار العيادة"
                  className="h-16 w-16 rounded-xl border border-white/10 bg-white object-contain p-1"
                />
              )}

              <label className="btn-ghost cursor-pointer">
                <Icon name="upload" size={16} />
                {form.logo ? 'تغيير الشعار' : 'رفع شعار'}
                <input type="file" accept="image/*" onChange={handleLogo} className="hidden" />
              </label>

              {form.logo && (
                <Button
                  variant="subtle"
                  icon="trash-2"
                  className="!text-red-300"
                  onClick={() => setForm((c) => ({ ...c, logo: '' }))}
                >
                  إزالة
                </Button>
              )}
            </div>
          </section>

          <PrescriptionDesigner form={form} setForm={setForm} doctorProfile={doctorProfile} />
        </div>
      )}
    </Modal>
  );
}

export default PrescriptionDesignerModal;
